import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AppShell } from "@/client/components/app-shell";
import { ApiError, apiQuery, useApiSuspenseQuery } from "@/client/lib/api";
import { ssoAction } from "@/client/modules/assistente/actions";
import { logoutAction, returnToAdminAction } from "@/client/modules/auth/actions";

/**
 * Layout de /app: exige login (senão vai para /entrar) e desenha o menu.
 * Módulo desligado em FEATURES responde 404 na API e o menu nem mostra.
 */
export const Route = createFileRoute("/app")({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.fetchQuery(apiQuery("auth", "me", {}));
    if (!me.user) throw redirect({ to: "/entrar", search: { de: location.pathname } as never });
  },
  component: AppLayout,
});

function AppLayout() {
  const { data: me } = useApiSuspenseQuery("auth", "me", {});
  const navigate = useNavigate();
  const [avisoAssistente, setAvisoAssistente] = useState<string | null>(null);

  /** Login único: abre a aba na hora (senão o navegador bloqueia o pop-up) e a leva ao painel quando o link chega. */
  async function abrirAssistente() {
    setAvisoAssistente(null);
    const aba = window.open("about:blank", "_blank");
    try {
      const { url } = await ssoAction();
      if (aba) {
        aba.opener = null;
        aba.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (e) {
      aba?.close();
      setAvisoAssistente(e instanceof ApiError ? e.message : "Não deu pra abrir o assistente. Tente de novo.");
    }
  }

  if (!me.user) {
    void navigate({ to: "/entrar", search: { de: undefined } });
    return null;
  }
  return (
    <AppShell
      user={me.user}
      canSwitch={me.canSwitch}
      isPlatformAdmin={me.isPlatformAdmin}
      onLogout={() => logoutAction()}
      onOpenAssistente={abrirAssistente}
    >
      {avisoAssistente && (
        <div className="mx-auto mb-4 max-w-7xl rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          {avisoAssistente}
        </div>
      )}
      {me.impersonating && (
        <div className="mx-auto mb-4 flex max-w-7xl items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <span>
            Visualizando como <strong>{me.user.displayName}</strong> (você é {me.impersonating.adminName}).
          </span>
          <button
            type="button"
            onClick={() => returnToAdminAction()}
            className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-100"
          >
            Voltar pro meu usuário
          </button>
        </div>
      )}
      <Outlet />
    </AppShell>
  );
}
